import React, { useState, useEffect, useRef } from 'react';
import { Chessboard } from 'react-chessboard';
import { motion, AnimatePresence } from 'framer-motion';
import './Hero.css';

const demoVideos = [
  {
    id: 4,
    title: 'Improve your Racing Skills',
    description: 'Model high-speed motion, occlusion recovery, and multi-agent interaction on the track.',
    thumbnail: '/scene_start-sports_karting.png',
    video: '/scene_animation-sports_karting.mp4',
    videoDelay: 4500,
    totalDuration: 6500,
    color: '#fecaca', // Light Red
  },
  {
    id: 1,
    title: 'Helper for experiments in your lab',
    description: 'Track the physics of an experimental pendulum in real-time, extracting precise mechanical parameters.',
    thumbnail: '/scene_start-experiment_pendulum.png',
    video: '/scene_animation-experiment_pendulum.mp4',
    videoDelay: 1500,
    totalDuration: 3500,
    color: '#fef08a', // Light Yellow
  },
  {
    id: 2,
    title: 'Record and Analyze your run',
    description: 'Analyze running biomechanics and predict trajectory with high reliability using bayesian mechanics.',
    thumbnail: '/scene_start-sport_running.png',
    video: '/scene_animation-sport_running.mp4',
    videoDelay: 5500,
    totalDuration: 7500,
    color: '#bbf7d0', // Light Green
  },
  {
    id: 3,
    title: 'Save your real-world chess games',
    description: 'Reason about strategic board states, pieces tracking, and complex semantic inference.',
    thumbnail: '/scene_start-sports_chess.png',
    video: '/scene_animation-sports_chess.mp4',
    videoDelay: 3500,
    totalDuration: 5500,
    color: '#bfdbfe', // Light Blue
  },
];

const windowOffsets = [
  { x: 0, y: 0 },
  { x: 40, y: 40 }, // Down and right
  { x: 80, y: 10 }, // Up and right 
  { x: 20, y: -15 }, // Up and left
];

const smoothCurve2D = (points) => {
  if (points.length < 2) return '';
  let d = `M ${points[0].x},${points[0].y}`;
  for (let i = 1; i < points.length - 1; i++) {
    const xc = (points[i].x + points[i + 1].x) / 2;
    const yc = (points[i].y + points[i + 1].y) / 2;
    d += ` Q ${points[i].x},${points[i].y} ${xc},${yc}`;
  }
  d += ` L ${points[points.length - 1].x},${points[points.length - 1].y}`;
  return d;
};

const PendulumOverlay = () => {
  const lines = [{"id":1,"text":"1.2m","x1":46.6,"y1":1.1,"x2":46.2,"y2":39.5},{"id":2,"text":"0.5m","x1":46.9,"y1":49.2,"x2":51.7,"y2":70.2}];
  const circles = [{"id":1,"cx":52.3,"cy":74.4,"r":3.4},{"id":2,"cx":45.8,"cy":43.7,"r":3}];

  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 10 }}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 1, 0] }}
        transition={{ delay: 0.5, duration: 2, times: [0, 0.2, 0.8, 1], ease: "easeInOut" }}
        style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
      >
        {lines.map((ln) => {
          const midX = (ln.x1 + ln.x2) / 2;
          const midY = (ln.y1 + ln.y2) / 2;
          const angle = Math.atan2(ln.y2 - ln.y1, ln.x2 - ln.x1) * 180 / Math.PI;
          return (
            <React.Fragment key={`line-${ln.id}`}>
              <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }}>
                <line x1={`${ln.x1}%`} y1={`${ln.y1}%`} x2={`${ln.x2}%`} y2={`${ln.y2}%`} stroke="#166534" strokeWidth="3" strokeDasharray="4 4" />
              </svg>
              <div
                style={{
                  position: 'absolute',
                  left: `${midX}%`,
                  top: `${midY}%`,
                  transform: `translate(-50%, -50%) rotate(${angle}deg) translateY(-15px)`,
                  color: '#166534',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  fontFamily: "'JetBrains Mono', monospace",
                  textShadow: '1px 1px 3px rgba(255,255,255,0.8), 0 0 10px #22c55e',
                  userSelect: 'none'
                }}
              >
                {ln.text}
              </div>
            </React.Fragment>
          );
        })}
        
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }}>
          {circles.map(c => (
            <g key={`circle-${c.id}`}>
              <circle cx={`${c.cx}%`} cy={`${c.cy}%`} r={`${c.r}%`} fill="rgba(34, 197, 94, 0.25)" stroke="#166534" strokeWidth="2.5" />
            </g>
          ))}
        </svg>
      </motion.div>
    </div>
  );
};

const RunningOverlay = () => {
  const points = [
    {"x":42.2,"y":36.3},
    {"x":52.8,"y":28.4},
    {"x":58.5,"y":29.2},
    {"x":57.6,"y":37.5},
    {"x":99.3,"y":86.2},
    {"x":100,"y":100},
    {"x":0,"y":100},
    {"x":0,"y":78.6}
  ];

  const polygonData = points.map(p => `${p.x},${p.y}`).join(' ');

  return (
    <div 
      style={{
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 5
      }}
    >
      {/* Running Animation: Road segmentation map */}
      <motion.svg 
        viewBox="0 0 100 100" 
        preserveAspectRatio="none"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 1, 0] }}
        transition={{ delay: 1, duration: 4.5, times: [0, 0.22, 0.78, 1], ease: "easeInOut" }}
        style={{
          position: 'absolute',
          top: 0, left: 0,
          width: '100%', height: '100%',
          pointerEvents: 'none'
        }}
      >
        <defs>
          <pattern id="pink-mesh" width="16" height="16" patternUnits="userSpaceOnUse" patternTransform="scale(1 0.3) rotate(45)">
            <path d="M 16 0 L 0 0 0 16" fill="none" stroke="#be185d" strokeWidth="1.5" />
          </pattern>
        </defs>
        <polygon points={polygonData} fill="rgba(244, 114, 182, 0.15)" stroke="#be185d" strokeWidth="0.5" />
        <polygon points={polygonData} fill="url(#pink-mesh)" />
      </motion.svg>

      {/* Running Animation: Glowing target line */}
      <motion.div
        initial={{ opacity: 0, scaleX: 0 }}
        animate={{ opacity: [0, 1, 1, 0], scaleX: [0, 1, 1, 1] }}
        transition={{ delay: 2, duration: 3.5, times: [0, 0.17, 0.83, 1], ease: "easeInOut" }}
        style={{
          position: 'absolute',
          top: '45%',
          left: '35%',
          width: '30%',
          height: '4px',
          backgroundColor: '#fef08a',
          boxShadow: '0 0 12px #eab308, 0 0 24px #eab308',
          borderRadius: '2px',
          zIndex: 10,
          transformOrigin: 'center',
          pointerEvents: 'none'
        }}
      />
      
      {/* Running Animation: Target text */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: [0, 1, 1, 0], y: [10, 0, 0, 0] }}
        transition={{ delay: 2.5, duration: 3, times: [0, 0.13, 0.87, 1], ease: "easeInOut" }}
        style={{
          position: 'absolute',
          top: '48%',
          left: '50%',
          transform: 'translateX(-50%)',
          color: '#fef08a',
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: '0.9rem',
          fontWeight: 'bold',
          textShadow: '1px 1px 3px rgba(0,0,0,0.9), 0 0 10px #eab308',
          whiteSpace: 'nowrap',
          zIndex: 10,
          pointerEvents: 'none'
        }}
      >
        800m, sector target
      </motion.div>
    </div>
  );
};

const KartingOverlay = () => {
  const points = [
    {"x":37.4,"y":39.8},
    {"x":66.7,"y":36.3},
    {"x":85.6,"y":34.7},
    {"x":53.4,"y":45.3},
    {"x":52.3,"y":52.7},
    {"x":100,"y":100},
    {"x":0,"y":99.4},
    {"x":0,"y":48.5}
  ];

  const curvePoints = [
    {"x":28.8,"y":100},
    {"x":31.2,"y":75.7},
    {"x":39.2,"y":52.4},
    {"x":49.1,"y":43.4},
    {"x":61.8,"y":39.5},
    {"x":73.9,"y":38.2},
    {"x":71.5,"y":35.6},
    {"x":51,"y":35.3}
  ];

  const polygonData = points.map(p => `${p.x},${p.y}`).join(' ');
  const curvePath = smoothCurve2D(curvePoints);

  return (
    <div 
      style={{
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 5
      }}
    >
      {/* Karting Animation: Track segmentation map */}
      <motion.svg 
        viewBox="0 0 100 100" 
        preserveAspectRatio="none"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 1, 0] }}
        transition={{ delay: 1, duration: 5.5, times: [0, 0.18, 0.82, 1], ease: "easeInOut" }}
        style={{
          position: 'absolute',
          top: 0, left: 0,
          width: '100%', height: '100%',
          pointerEvents: 'none'
        }}
      >
        <defs>
          <pattern id="red-mesh" width="16" height="16" patternUnits="userSpaceOnUse" patternTransform="scale(1 0.3) rotate(45)">
            <path d="M 16 0 L 0 0 0 16" fill="none" stroke="#ef4444" strokeWidth="1.5" />
          </pattern>
        </defs>
        <polygon points={polygonData} fill="rgba(239, 68, 68, 0.15)" stroke="#ef4444" strokeWidth="0.5" />
        <polygon points={polygonData} fill="url(#red-mesh)" />
      </motion.svg>

      {/* Karting Animation: Glowing Bezier Curve */}
      <motion.svg 
        viewBox="0 0 100 100" 
        preserveAspectRatio="none"
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{ delay: 5.5, duration: 1, ease: "easeInOut" }}
        style={{
          position: 'absolute',
          top: 0, left: 0,
          width: '100%', height: '100%',
          pointerEvents: 'none',
          zIndex: 10,
          filter: 'drop-shadow(0px 0px 8px rgba(255, 255, 255, 0.8))'
        }}
      >
        <motion.path 
          d={curvePath} 
          fill="none" 
          stroke="white" 
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeDasharray="300"
          initial={{ strokeDashoffset: 300 }}
          animate={{ strokeDashoffset: 0 }}
          transition={{ delay: 2, duration: 2, ease: "linear" }}
        />
      </motion.svg>

      {/* Karting Animation: Text near line */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 1, 0] }}
        transition={{ delay: 3, duration: 3.5, times: [0, 0.28, 0.71, 1], ease: "easeInOut" }}
        style={{
          position: 'absolute',
          top: '65%',
          left: '20%',
          color: 'white',
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: '0.75rem',
          fontWeight: 'bold',
          textShadow: '1px 1px 3px rgba(0,0,0,0.9)',
          zIndex: 10,
          pointerEvents: 'none'
        }}
      >
        previous<br />racing line
      </motion.div>

      {/* Karting Animation: Top left tabs */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: [0, 1, 1, 0], x: [-20, 0, 0, 0] }}
        transition={{ delay: 4.5, duration: 2, times: [0, 0.1, 0.8, 1], ease: "easeInOut" }}
        style={{
          position: 'absolute',
          top: '20px',
          left: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          zIndex: 20,
          pointerEvents: 'none',
        }}
      >
        <div style={{ background: '#eab308', color: '#1a1a24', padding: '6px 12px', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px', fontFamily: "'JetBrains Mono', monospace" }}>
          <span style={{ background: '#1a1a24', color: '#eab308', borderRadius: '50%', width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem' }}>!</span>
          early breaking
        </div>
        <div style={{ background: '#eab308', color: '#1a1a24', padding: '6px 12px', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px', fontFamily: "'JetBrains Mono', monospace" }}>
          <span style={{ background: '#1a1a24', color: '#eab308', borderRadius: '50%', width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem' }}>!</span>
          early approach to apex
        </div>
        <div style={{ background: '#1a1a24', color: '#fff', padding: '6px 12px', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.85rem', marginTop: '4px', fontFamily: "'JetBrains Mono', monospace", width: 'fit-content' }}>
          Velocity: 14 km/h
        </div>
      </motion.div>
    </div>
  );
};

const ChessOverlay = () => {
  const boxes = [{"x":76.3,"y":52.7,"w":3.1,"h":13.6},{"x":81.1,"y":55.3,"w":4.2,"h":13.6},{"x":50.4,"y":41.4,"w":3.4,"h":16},{"x":82.6,"y":49.2,"w":3.6,"h":17.4},{"x":36.6,"y":57.6,"w":4.7,"h":19.3},{"x":62.7,"y":39.5,"w":3.9,"h":19},{"x":53.3,"y":47.6,"w":3.5,"h":9.3},{"x":69.3,"y":55,"w":3.2,"h":11.7},{"x":37.6,"y":60.5,"w":4.5,"h":21.9},{"x":43,"y":56,"w":5.5,"h":29.7},{"x":48.6,"y":54,"w":4,"h":14.5},{"x":61.4,"y":53.7,"w":4,"h":15.2},{"x":32,"y":57.9,"w":3.8,"h":14.8},{"x":51.2,"y":69.8,"w":5.6,"h":19.4},{"x":63.4,"y":71.5,"w":4.9,"h":16.1},{"x":71.4,"y":74.1,"w":5.7,"h":18.1}];

  const [fen, setFen] = useState("r2k3r/1p4pp/5p2/4n3/2N5/8/PP4PP/2BK1R2 w - - 0 1");

  useEffect(() => {
    const t = setTimeout(() => {
      setFen("r2k3r/1p4pp/5p2/4n3/2N5/8/PP1K2PP/2B2R2 b - - 1 1");
    }, 4500); 
    return () => clearTimeout(t);
  }, []);

  return (
    <div 
      style={{
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 5
      }}
    >
      {/* Static Bounding Boxes fading out later */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 1, 0] }}
        transition={{ delay: 0.5, duration: 2, times: [0, 0.2, 0.8, 1], ease: "easeInOut" }}
        style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
      >
        {boxes.map((b, idx) => (
          <div 
            key={idx}
            style={{
              position: 'absolute',
              left: `${b.x}%`,
              top: `${b.y}%`,
              width: `${b.w}%`,
              height: `${b.h}%`,
              border: '2px solid #22c55e',
              backgroundColor: 'transparent',
              zIndex: 20
            }}
          />
        ))}
      </motion.div>

      {/* Chessboard top-left corner */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0 }}
        transition={{ delay: 2.5, duration: 0.5 }}
        style={{
          position: 'absolute',
          top: '20px',
          left: '20px',
          width: '162px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          borderRadius: '4px',
          overflow: 'hidden',
          zIndex: 15
        }}
      >
        <Chessboard 
          options={{
            position: fen,
            arePiecesDraggable: false,
            darkSquareStyle: { backgroundColor: '#22c55e' },
            lightSquareStyle: { backgroundColor: '#ffffff' },
            showNotation: false
          }}
        />
      </motion.div>
    </div>
  );
};

const HeroVideo = ({ video, isActive }) => {
  const [showVideo, setShowVideo] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    let timeout;
    if (isActive) {
      timeout = setTimeout(() => {
        setShowVideo(true);
        if (videoRef.current) {
          videoRef.current.currentTime = 0;
          videoRef.current.play().catch(e => console.error('Video play error:', e));
        }
      }, video.videoDelay);
    } else {
      setShowVideo(false);
      if (videoRef.current) {
        videoRef.current.pause();
      }
    }
    return () => clearTimeout(timeout);
  }, [isActive, video.videoDelay]);

  return (
    <div 
      className="hero__main-video"
      style={{
        backgroundColor: video.color,
        backgroundImage: `url(${video.thumbnail})`,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <video
        ref={videoRef}
        src={video.video}
        muted
        playsInline
        style={{
          position: 'absolute',
          top: 0, left: 0, width: '100%', height: '100%',
          objectFit: 'cover',
          zIndex: 2,
          opacity: showVideo ? 1 : 0,
          transition: 'opacity 0.5s ease-in-out'
        }}
      />
      <div style={{ position: 'absolute', inset: 0, zIndex: 10 }}>
        {video.id === 1 && isActive && <PendulumOverlay />}
        {video.id === 2 && isActive && <RunningOverlay />}
        {video.id === 4 && isActive && <KartingOverlay />}
        {video.id === 3 && isActive && <ChessOverlay />}
      </div>
    </div>
  );
};

export default function Hero() {
  const maxWindows = demoVideos.length;
  const [queue, setQueue] = useState([0]);
  const [versions, setVersions] = useState({ 0: 1 });
  const [clickKey, setClickKey] = useState(0);

  const activeIndex = queue[queue.length - 1];

  const activateWindow = (index) => {
    setQueue(prev => {
      const newQ = prev.filter(i => i !== index);
      newQ.push(index);
      return newQ;
    });
    setVersions(prev => ({
      ...prev,
      [index]: (prev[index] || 0) + 1
    }));
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      activateWindow((activeIndex + 1) % maxWindows);
    }, demoVideos[activeIndex].totalDuration);
    
    return () => clearTimeout(timeoutId);
  }, [activeIndex, clickKey, versions]);

  const openWindows = queue.map((videoIndex, stackIndex) => {
    return {
      video: demoVideos[videoIndex],
      videoIndex: videoIndex,
      key: `${demoVideos[videoIndex].id}-${versions[videoIndex]}`,
      stackIndex: stackIndex,
      isActive: stackIndex === queue.length - 1
    };
  });

  const handleThumbClick = (index) => {
    setClickKey(prev => prev + 1);
    if (index === activeIndex) {
      setVersions(prev => ({
        ...prev,
        [index]: prev[index] + 1
      }));
    } else {
      activateWindow(index);
    }
  };

  return (
    <section className="hero" id="product">
      <div className="hero__content">
        <h1 className="hero__title">
          BayesMech 
          <br />
          <span className="hero__rotating-wrapper">
            <span 
               className="hero__rotating-word" 
               style={{ backgroundColor: demoVideos[activeIndex].color }}
            >
              vision
            </span>
          </span>
        </h1>

        <p className="hero__subtitle">
          Visual Intelligence, that generates real world actionable insights
        </p>

        <div className="hero__actions">
          <a href="#build" className="hero__btn hero__btn--primary">
            Join Early Access
          </a>
          {/* <a href="#vision" className="hero__btn hero__btn--secondary">
            Read our Engineering Blog
          </a> */}
        </div>
      </div>

      <div className="hero__visual">
        <div className="hero__visual-stack">
          <AnimatePresence>
            {openWindows.map((w) => (
              <motion.div
                key={w.key}
                className="hero__main-video-container"
                initial={{ scale: 0.8, opacity: 0, y: 30 }}
                animate={{ 
                  scale: 1, 
                  opacity: 1, 
                  x: windowOffsets[w.videoIndex]?.x || 0, 
                  y: windowOffsets[w.videoIndex]?.y || 0, 
                  zIndex: w.stackIndex 
                }}
                exit={{ scale: 0.9, opacity: 0, y: 30, transition: { duration: 0.25 } }}
                transition={{ type: 'spring', damping: 22, stiffness: 180 }}
                style={{
                  position: 'absolute',
                  top: 20,
                  left: 0,
                  width: 'calc(100% - 80px)',
                  height: 'calc(100% - 40px)',
                }}
              >
                <div className="hero__window-header">
                  <div className="hero__window-dot" style={{ backgroundColor: w.video.color }}></div>
                  <div className="hero__window-title">{w.video.title}</div>
                </div>
                  <HeroVideo key={`${w.video.id}-${w.isActive ? clickKey : ''}`} video={w.video} isActive={w.isActive} />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        <div className="hero__thumbnails">
          {demoVideos.map((video, index) => {
            const isActive = index === activeIndex;
            return (
              <div
                key={video.id}
                className={`hero__thumb ${isActive ? 'active' : ''}`}
                style={{
                  backgroundColor: video.color,
                  backgroundImage: `url(${video.thumbnail})`
                }}
                onClick={() => handleThumbClick(index)}
              >
                <div className="hero__thumb-overlay">{video.title}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
